import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  completeWhatsAppSignup,
  listPendingPages,
  selectMetaPage,
  startMetaOAuth,
} from '@/api/meta-oauth';
import { queryKeys } from '@/lib/query-keys';
import type { MetaOAuthChannel } from '@/types/integrations';

/**
 * Onboarding OAuth de Meta (módulo 2.1) para conectar Messenger e Instagram sin pegar
 * credenciales a mano. El flujo completo está en `docs/ONBOARDING_OAUTH.md` y el paso central
 * NO pasa por el frontend: Meta redirige el navegador al callback público del backend, que a
 * su vez devuelve al panel con `?oauth=…` (ver `meta-oauth-redirect.ts`).
 *
 * Requisitos del backend que la pantalla no puede suplir: `META_APP_ID`, `META_APP_SECRET`,
 * `META_OAUTH_REDIRECT_URI` (y `META_OAUTH_FRONTEND_REDIRECT` para volver al panel). Si
 * faltan, `start` responde 400 con un mensaje explícito ("El onboarding OAuth no está
 * configurado…"): se muestra tal cual y se ofrece la carga manual de credenciales.
 */

/** `GET /api/integrations/oauth/:tenantId/meta/start?channel=…` (solo ADMIN). */
export function useStartMetaOAuth(tenantId: string | null) {
  return useMutation({
    mutationFn: (channel: MetaOAuthChannel) => startMetaOAuth(tenantId ?? '', channel),
  });
}

/** `GET .../meta/pending/:ticket` (solo ADMIN) — Pages candidatas de una sesión pendiente. */
export function usePendingPages(tenantId: string | null, ticket: string | null) {
  return useQuery({
    queryKey: queryKeys.integrations.oauthPending(tenantId ?? '', ticket ?? ''),
    queryFn: () => listPendingPages(tenantId ?? '', ticket ?? ''),
    enabled: Boolean(tenantId) && Boolean(ticket),
    retry: false,
  });
}

/** `POST .../meta/select` (solo ADMIN) — cierra la conexión con la Page elegida. */
export function useSelectMetaPage(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticket, pageId }: { ticket: string; pageId: string }) =>
      selectMetaPage(tenantId ?? '', ticket, pageId),
    onSuccess: () => {
      if (tenantId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.integrations.overview(tenantId) });
      }
    },
  });
}

/**
 * `POST .../meta/whatsapp/complete` (solo ADMIN) — cierra el onboarding de WhatsApp con Embedded
 * Signup. Al terminar invalida el resumen de integraciones para que la tarjeta pase a «Conectado».
 */
export function useCompleteWhatsAppSignup(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { code: string; wabaId: string; phoneNumberId: string; pin?: string }) =>
      completeWhatsAppSignup(tenantId ?? '', payload),
    onSuccess: () => {
      if (tenantId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.integrations.overview(tenantId) });
      }
    },
  });
}
